/**
 * Simplified Reynolds-style boids (sep / align / coh) for compare mode.
 * Uses spatial hash when provided (SIM-07) — DESIGNS-TRACK-X §5.
 */

import type { ModelPlugin } from "../types";

const SEP_W = 1.2;
const ALIGN_W = 0.8;
const COH_W = 0.6;
const MAX_SPEED = 0.08;

export const reynoldsModel: ModelPlugin = {
  id: "reynolds",
  label: "Reynolds boids (simple)",
  tier: "lab",
  description:
    "Local sep/align/coh neighborhood — contrast with friends/enemies",
  step({ params, positions, outPositions, spatialHash }) {
    const { n, dim } = params;
    const radius = params.neighborRadius > 0 ? params.neighborRadius : 0.35;
    const r2 = radius * radius;
    const useHash = spatialHash != null && dim >= 2;

    for (let i = 0; i < n; i++) {
      let sepX = 0,
        sepY = 0,
        sepZ = 0;
      let aliX = 0,
        aliY = 0,
        aliZ = 0;
      let cohX = 0,
        cohY = 0,
        cohZ = 0;
      let count = 0;
      const ix = positions[i * dim]!;
      const iy = positions[i * dim + 1]!;
      const iz = dim > 2 ? positions[i * dim + 2]! : 0;

      const accumulate = (j: number, d2: number) => {
        const jx = positions[j * dim]!;
        const jy = positions[j * dim + 1]!;
        const jz = dim > 2 ? positions[j * dim + 2]! : 0;
        const dx = ix - jx;
        const dy = iy - jy;
        const dz = iz - jz;
        count++;
        const inv = 1 / Math.max(d2, 1e-12);
        sepX += dx * inv;
        sepY += dy * inv;
        sepZ += dz * inv;
        cohX += jx;
        cohY += jy;
        cohZ += jz;
        aliX += jx - ix;
        aliY += jy - iy;
        aliZ += jz - iz;
      };

      if (useHash) {
        spatialHash!.forNeighbors(i, positions, dim, r2, accumulate);
      } else {
        for (let j = 0; j < n; j++) {
          if (i === j) continue;
          const jx = positions[j * dim]!;
          const jy = positions[j * dim + 1]!;
          const jz = dim > 2 ? positions[j * dim + 2]! : 0;
          const dx = ix - jx;
          const dy = iy - jy;
          const dz = iz - jz;
          const d2 = dx * dx + dy * dy + dz * dz;
          if (d2 > r2 || d2 < 1e-12) continue;
          accumulate(j, d2);
        }
      }

      let fx = 0,
        fy = 0,
        fz = 0;
      if (count > 0) {
        fx += SEP_W * sepX + ALIGN_W * (aliX / count);
        fy += SEP_W * sepY + ALIGN_W * (aliY / count);
        fz += SEP_W * sepZ + ALIGN_W * (aliZ / count);
        const cx = cohX / count - ix;
        const cy = cohY / count - iy;
        const cz = cohZ / count - iz;
        fx += COH_W * cx;
        fy += COH_W * cy;
        fz += COH_W * cz;
      }
      fx += (params.contraction - 1) * ix * 0.5;
      fy += (params.contraction - 1) * iy * 0.5;
      if (dim > 2) fz += (params.contraction - 1) * iz * 0.5;

      let sp = Math.sqrt(fx * fx + fy * fy + fz * fz);
      if (sp > MAX_SPEED && sp > 0) {
        const s = MAX_SPEED / sp;
        fx *= s;
        fy *= s;
        fz *= s;
      }
      outPositions[i * dim] = ix + fx;
      outPositions[i * dim + 1] = iy + fy;
      if (dim > 2) outPositions[i * dim + 2] = iz + fz;
    }
  },
};
